import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IN_PERSON_NOTE_ID, NOTE_TYPE, PRIVATE_EXTENSION_BASE_URL } from 'utils';
import { GenericNoteList } from '../../shared/components/generic-notes-list/GenericNoteList';
import { GenericNotesConfig } from '../../shared/components/generic-notes-list/types';

export const useIntakeNotesModal = (): {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  IntakeNotesModal: React.FC<IntakeNotesModalProps>;
} => {
  const [isOpen, setIsOpen] = React.useState(false);

  const openModal = (): void => setIsOpen(true);
  const closeModal = (): void => setIsOpen(false);

  return {
    isOpen,
    openModal,
    closeModal,
    IntakeNotesModal,
  };
};

interface IntakeNotesModalProps {
  open: boolean;
  onClose: () => void;
}

const IntakeNotesModal: React.FC<IntakeNotesModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Box px={3} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" component="div">
            {t('telemed.chart.intakeNotes')}
          </Typography>
          <IconButton sx={{ color: 'grey.500' }} edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: -3 }}>
        <IntakeNotes />
      </DialogContent>
    </Dialog>
  );
};

const getIntakeNotesConfig = (t: any): GenericNotesConfig => ({
  apiConfig: {
    fieldName: 'notes',
    type: NOTE_TYPE.INTAKE,
    searchParams: {
      _search_by: 'encounter',
      _sort: '-_lastUpdated',
      _count: 1000,
      _tag: `${PRIVATE_EXTENSION_BASE_URL}/${NOTE_TYPE.INTAKE}|${IN_PERSON_NOTE_ID}`,
    },
  },
  locales: {
    entityLabel: t('telemed.chart.intakeNoteLabel'),
    editModalTitle: t('telemed.chart.editIntakeNote'),
    editModalPlaceholder: t('telemed.chart.intakeNoteLabel'),
    getAddButtonText: (isSaving: boolean) =>
      isSaving ? t('telemed.chart.savingIntakeNote') : t('telemed.chart.saveIntakeNote'),
    getMoreButtonText: (isMoreEntitiesShown: boolean) =>
      isMoreEntitiesShown ? t('telemed.chart.seeLess') : t('telemed.chart.seeMore'),
    getDeleteModalTitle: (entityLabel: string) => t('telemed.chart.deleteEntity', { entityLabel }),
    getDeleteModalContent: (entityLabel: string) => t('telemed.chart.deleteEntityConfirm', { entityLabel }),
    getKeepButtonText: () => t('telemed.chart.keep'),
    getDeleteButtonText: (isDeleting: boolean) =>
      isDeleting ? t('telemed.chart.deleting') : t('telemed.chart.delete'),
    getLeaveButtonText: () => t('telemed.chart.leave'),
    getSaveButtonText: (isSaving: boolean) => (isSaving ? t('telemed.chart.saving') : t('telemed.chart.save')),
    getErrorMessage: (action: string, entityLabel: string) => t('telemed.chart.errorAction', { action, entityLabel }),
    getGenericErrorMessage: () => t('telemed.chart.errorGeneric'),
  },
});

export const IntakeNotes: React.FC = () => {
  const { t } = useTranslation();
  const intakeNotesConfig = getIntakeNotesConfig(t);
  return (
    <GenericNoteList
      separateEncounterNotes={false}
      apiConfig={intakeNotesConfig.apiConfig}
      locales={intakeNotesConfig.locales}
    />
  );
};
